import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import contentData from '../data/content.json';
import apiClient from '../services/apiClient';

const ContentContext = createContext();
const PUT_DEBOUNCE_MS = 400;

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isOnlineMode, setIsOnlineMode] = useState(false);
  const [needsInitialUpload, setNeedsInitialUpload] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState('offline');
  const [contentFromSeed, setContentFromSeed] = useState(false);
  const [syncError, setSyncError] = useState(null);

  const contentRef = useRef(null);
  const putTimersRef = useRef({});
  const pendingValuesRef = useRef({});

  useEffect(() => {
    contentRef.current = content;
  }, [content]);

  const canWriteToCms = useCallback(() => {
    return (
      connectionStatus === 'online' &&
      !contentFromSeed &&
      apiClient.getStoredToken() &&
      !apiClient.isTokenExpired()
    );
  }, [connectionStatus, contentFromSeed]);

  const checkBackendConnection = async () => {
    try {
      const response = await fetch(`${apiClient.baseURL}/health`);
      if (response.ok) {
        setConnectionStatus('online');
        if (apiClient.getStoredToken() && !apiClient.isTokenExpired()) {
          setIsOnlineMode(true);
        }
        return true;
      }
      throw new Error('Health check failed');
    } catch (error) {
      console.error('SQLite backend not available:', error.message);
      setConnectionStatus('offline');
      setIsOnlineMode(false);
      return false;
    }
  };

  const cancelPendingWrites = useCallback(() => {
    Object.values(putTimersRef.current).forEach(clearTimeout);
    putTimersRef.current = {};
    pendingValuesRef.current = {};
    setHasUnsavedChanges(false);
    setSyncError(null);
  }, []);

  const loadContent = async ({ resetPending = true } = {}) => {
    if (resetPending) {
      cancelPendingWrites();
    }
    setIsLoading(true);
    try {
      console.log('📡 Loading content from CMS backend...');

      const hasValidToken = apiClient.getStoredToken() && !apiClient.isTokenExpired();
      let response = await apiClient.getPublicContent();

      if (!response.success && hasValidToken) {
        response = await apiClient.getAllContent();
      }

      if (response.success && response.content && Object.keys(response.content).length > 0) {
        setContent(response.content);
        setConnectionStatus('online');
        setIsOnlineMode(hasValidToken);
        setContentFromSeed(false);
        if (resetPending) {
          setHasUnsavedChanges(false);
          setSyncError(null);
        }
        console.log('✅ Content loaded from CMS backend (no local seed merge)');
      } else {
        throw new Error('Backend has no content');
      }
    } catch (error) {
      console.warn('📄 CMS backend unavailable, using bundled seed as last resort:', error.message);
      setContent(contentData);
      setConnectionStatus('offline');
      setIsOnlineMode(false);
      setContentFromSeed(true);
    } finally {
      setIsLoading(false);
    }
  };

  const applyLocalUpdate = (path, value) => {
    setContent((prevContent) => {
      const pathArray = path.split('.');
      const newContent = JSON.parse(JSON.stringify(prevContent || {}));

      let current = newContent;
      for (let i = 0; i < pathArray.length - 1; i++) {
        const key = pathArray[i];
        const nextKey = pathArray[i + 1];
        if (!current[key]) {
          current[key] = /^\d+$/.test(nextKey) ? [] : {};
        }
        current = current[key];
      }

      current[pathArray[pathArray.length - 1]] = value;
      return newContent;
    });
  };

  const flushPendingWrites = async () => {
    Object.values(putTimersRef.current).forEach(clearTimeout);
    putTimersRef.current = {};

    const pending = { ...pendingValuesRef.current };
    pendingValuesRef.current = {};

    for (const [path, value] of Object.entries(pending)) {
      await apiClient.updateContentAtPath(path, value);
      console.log(`✅ Flushed pending update: ${path}`);
    }
  };

  const schedulePut = (path, value) => {
    pendingValuesRef.current[path] = value;

    if (putTimersRef.current[path]) {
      clearTimeout(putTimersRef.current[path]);
    }

    putTimersRef.current[path] = setTimeout(async () => {
      delete putTimersRef.current[path];
      const val = pendingValuesRef.current[path];
      delete pendingValuesRef.current[path];

      try {
        await apiClient.updateContentAtPath(path, val);
        console.log(`✅ Content updated in CMS backend: ${path}`);
        setSyncError(null);
      } catch (error) {
        const message = `Échec de la synchronisation (${path}): ${error.message}`;
        console.warn(`⚠️ ${message}`);
        setSyncError(message);
      }
    }, PUT_DEBOUNCE_MS);
  };

  const updateContent = async (path, value) => {
    if (connectionStatus === 'offline' || contentFromSeed) {
      setSyncError(
        'Sauvegarde bloquée : le CMS n\'est pas chargé depuis le serveur. Rechargez la page.'
      );
      return;
    }

    if (!apiClient.getStoredToken() || apiClient.isTokenExpired()) {
      setSyncError('Session expirée — reconnectez-vous pour enregistrer les modifications.');
      return;
    }

    applyLocalUpdate(path, value);
    setHasUnsavedChanges(true);
    schedulePut(path, value);
  };

  const getContent = useCallback(
    (path) => {
      if (!content) return undefined;

      const pathArray = path.split('.');
      let current = content;

      for (const key of pathArray) {
        if (current && typeof current === 'object' && key in current) {
          current = current[key];
        } else {
          return undefined;
        }
      }

      return current;
    },
    [content]
  );

  const saveContent = async () => {
    if (!contentRef.current) {
      return { success: false, message: 'Aucun contenu à sauvegarder.' };
    }

    if (connectionStatus === 'offline' || contentFromSeed) {
      return {
        success: false,
        message:
          'Sauvegarde bloquée : le CMS n\'est pas chargé depuis le serveur. Rechargez la page ou vérifiez la connexion avant d\'enregistrer.',
      };
    }

    if (!apiClient.getStoredToken() || apiClient.isTokenExpired()) {
      return {
        success: false,
        message: 'Session expirée — reconnectez-vous pour sauvegarder.',
      };
    }

    setIsLoading(true);
    try {
      await flushPendingWrites();

      console.log('💾 Saving content to CMS backend...');
      const response = await apiClient.saveAllContent(contentRef.current);

      if (response.success) {
        const hadMoreEdits = Object.keys(pendingValuesRef.current).length > 0;
        setHasUnsavedChanges(hadMoreEdits);
        console.log('✅ Content saved to CMS backend successfully');
        if (!hadMoreEdits) {
          await loadContent();
        }
        return {
          success: true,
          message: response.message || 'Contenu sauvegardé avec succès — visible sur le site immédiatement.',
        };
      }

      throw new Error(response.error || response.message || 'Backend save failed');
    } catch (error) {
      console.error('❌ Failed to save to CMS backend:', error);
      return {
        success: false,
        message: `❌ Erreur: Le backend est inaccessible. Erreur: ${error.message}`,
      };
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, []);

  useEffect(() => {
    const interval = setInterval(checkBackendConnection, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    return () => {
      Object.values(putTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  const reloadContentAfterAuth = async () => {
    console.log('🔄 Reloading content after authentication...');
    await loadContent();
  };

  const value = {
    content,
    updateContent,
    getContent,
    saveContent,
    isLoading,
    hasUnsavedChanges,
    isOnlineMode,
    needsInitialUpload,
    setNeedsInitialUpload,
    connectionStatus,
    contentFromSeed,
    syncError,
    canWriteToCms,
    checkBackendConnection,
    loadContent,
    cancelPendingWrites,
    reloadContentAfterAuth,
  };

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};
